import React from "react";
import { Link } from "react-router-dom";
import type { Campaign, CampaignRecipient } from "../api/campaigns";
import { formatDateTime } from "../utils/dateFormat";
export const campaignDate = (value: string | null) =>
  value ? formatDateTime(value) : "—";
const date = campaignDate;
const buttonClass = "btn btn-neutral";
export const campaignRate = (value: number | null) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("de-AT", {
        style: "percent",
        maximumFractionDigits: 1,
      }).format(value);
export function campaignRates(campaign: Campaign) {
  const { metrics: m, channel, draft } = campaign;
  return [
    {
      label: "Öffnungsrate",
      value: draft ? null : m.openRate,
      detail: `${m.opened} geöffnet · ${
        channel === "EMAIL"
          ? `${m.delivered} zugestellt`
          : `${m.accepted} von FCM angenommen`
      }`,
    },
    {
      label: "Klickrate",
      value: draft || channel !== "EMAIL" ? null : m.clickRate,
      detail: `${m.clicked} geklickt · ${m.delivered} zugestellt`,
    },
    {
      label: "Bounce-Rate",
      value:
        draft || channel !== "EMAIL" || !m.accepted
          ? null
          : m.bounced / m.accepted,
      detail: `${m.bounced} Bounces · ${m.accepted} angenommen`,
    },
  ];
}
export const campaignName = (c: Campaign) =>
  c.campaignName || c.title || "Unbenannter Entwurf";
export const campaignPath = (c: Campaign) =>
  `/notifications/${encodeURIComponent(c.id)}${c.draft ? "/edit" : ""}`;
export function CampaignPager({
  page,
  last,
  onPage,
  disabled = false,
}: {
  page: number;
  last: boolean;
  disabled?: boolean;
  onPage: (p: number) => void;
}) {
  return (
    <div className="mt-3 flex items-center justify-end gap-3 text-sm">
      <button
        className={buttonClass}
        disabled={disabled || page === 0}
        onClick={() => onPage(page - 1)}
      >
        Zurück
      </button>
      <span>Seite {page + 1}</span>
      <button
        className={buttonClass}
        disabled={disabled || last}
        onClick={() => onPage(page + 1)}
      >
        Weiter
      </button>
    </div>
  );
}
export function CampaignRecipientTable({
  rows,
}: {
  rows: CampaignRecipient[];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b">
            <th className="p-2">Empfänger</th>
            <th>Status</th>
            <th>Angenommen</th>
            <th>Zugestellt</th>
            <th>Geöffnet</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.recipientId || r.userId || r.leadId || r.email} className="border-b align-top">
              <td className="p-2">
                {r.userId ? <Link
                  className="font-semibold underline"
                  to={`/users/${encodeURIComponent(r.userId)}`}
                >
                  {r.name || r.email}
                </Link> : <span className="font-semibold">{r.name || r.email}</span>}
                <div className="text-xs text-gray-500">{r.email}</div>
              </td>
              <td className="p-2">
                {r.status}
                <div className="text-xs text-gray-500">{r.reason}</div>
              </td>
              <td className="p-2 whitespace-nowrap">{date(r.acceptedAt)}</td>
              <td className="p-2 whitespace-nowrap">{date(r.deliveredAt)}</td>
              <td className="p-2 whitespace-nowrap">
                {date(r.openedAt || r.unknownOpenedAt)}
                {!r.openedAt && r.unknownOpenedAt && (
                  <div className="text-xs text-gray-500">Client unbekannt</div>
                )}
              </td>
              <td className="p-2">
                <details>
                  <summary className="cursor-pointer">Verlauf</summary>
                  <div className="min-w-[260px] space-y-1 py-2">
                    <div>Klick: {date(r.clickedAt)}</div>
                    <div>Abmeldung: {date(r.unsubscribedAt)}</div>
                    <div>Bounce: {date(r.bouncedAt)}</div>
                    <div>Beschwerde: {date(r.complainedAt)}</div>
                    <div>Bot-Öffnung: {date(r.botOpenedAt)}</div>
                    <div>Unbekannte Öffnung: {date(r.unknownOpenedAt)}</div>
                    <div>Bot-Klick: {date(r.botClickedAt)}</div>
                    <div>Unbekannter Klick: {date(r.unknownClickedAt)}</div>
                    {r.communication?.deliveries.map((d, i) => (
                      <div key={`d${i}`}>
                        Versuch {date(d.attemptedAt)} · {d.status} {d.errorCode}{" "}
                        {d.errorMessage}
                      </div>
                    ))}
                    {r.communication?.events.map((e, i) => (
                      <div key={`e${i}`}>
                        {date(e.occurredAt)} · {e.type}{" "}
                        {e.human === false
                          ? "(Bot)"
                          : e.human === null
                          ? "(Client unbekannt)"
                          : ""}{" "}
                        {e.reason}
                      </div>
                    ))}
                  </div>
                </details>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && (
        <p className="py-4 text-gray-500">Keine Empfänger für diesen Filter.</p>
      )}
    </div>
  );
}
